import { useEffect, useState } from "react";
import { Search, Star, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money, StatusBadge } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => api.get(`/admin/products${q ? `?q=${q}` : ""}`).then((r) => setProducts(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const save = async () => {
    try {
      await api.put(`/admin/products/${edit.id}`, {
        title: edit.title, description: edit.description, selling_price: parseFloat(edit.selling_price),
        category: edit.category, status: edit.status, featured: edit.featured, on_sale: edit.on_sale,
        seo_title: edit.seo_title, seo_description: edit.seo_description,
        tags: typeof edit.tags === "string" ? edit.tags.split(",").map((t) => t.trim()) : edit.tags,
      });
      toast.success("Product updated"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleField = async (p, field) => {
    try { await api.put(`/admin/products/${p.id}`, { [field]: !p[field] }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Products</h1><p className="text-sm text-zinc-500">{products.length} products</p></div>
      </div>
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="product-search" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 bg-zinc-50 border-b border-zinc-200">
            <th className="p-3 font-medium">Product</th><th className="p-3 font-medium">Cost → Price</th>
            <th className="p-3 font-medium">Margin</th><th className="p-3 font-medium">Stock</th>
            <th className="p-3 font-medium">Supplier</th><th className="p-3 font-medium">Featured</th>
            <th className="p-3 font-medium">Status</th><th className="p-3 font-medium"></th>
          </tr></thead>
          <tbody>
            {products.map((p) => {
              const margin = p.supplier_price ? Math.round((p.selling_price - p.supplier_price) / p.supplier_price * 100) : 0;
              return (
                <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`product-row-${p.slug}`}>
                  <td className="p-3"><div className="flex items-center gap-3">
                    <img src={(p.images || [])[0]} alt="" className="w-10 h-12 object-cover rounded-md" />
                    <div><p className="font-medium line-clamp-1 max-w-[180px]">{p.title}</p><p className="text-xs text-zinc-400 capitalize">{p.category}</p></div>
                  </div></td>
                  <td className="p-3 font-mono text-xs">{money(p.supplier_price)} → <span className="font-semibold">{money(p.selling_price)}</span></td>
                  <td className="p-3 text-green-600 font-medium">{margin}%</td>
                  <td className="p-3">{p.stock}</td>
                  <td className="p-3 text-xs text-zinc-500 capitalize">{p.supplier_name}</td>
                  <td className="p-3"><button onClick={() => toggleField(p, "featured")} data-testid={`feature-${p.slug}`}><Star size={16} className={p.featured ? "fill-amber-400 text-amber-400" : "text-zinc-300"} /></button></td>
                  <td className="p-3"><StatusBadge status={p.status} /></td>
                  <td className="p-3"><div className="flex gap-1">
                    <button onClick={() => setEdit({ ...p, tags: (p.tags || []).join(", ") })} className="p-1.5 hover:bg-zinc-100 rounded-md" data-testid={`edit-${p.slug}`}><Edit size={15} /></button>
                    <button onClick={() => del(p.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-md"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit Product</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="edit-title" /></div>
              <div><Label>Description</Label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Selling Price (USD)</Label><Input type="number" step="0.01" value={edit.selling_price} onChange={(e) => setEdit({ ...edit, selling_price: e.target.value })} data-testid="edit-price" /></div>
                <div><Label>Category</Label><Input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></div>
              </div>
              <div><Label>Tags (comma separated)</Label><Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} /></div>
              <div><Label>SEO Title</Label><Input value={edit.seo_title || ""} onChange={(e) => setEdit({ ...edit, seo_title: e.target.value })} /></div>
              <div><Label>SEO Description</Label><Textarea value={edit.seo_description || ""} onChange={(e) => setEdit({ ...edit, seo_description: e.target.value })} /></div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2"><Switch checked={edit.on_sale} onCheckedChange={(v) => setEdit({ ...edit, on_sale: v })} /><Label>On Sale</Label></div>
                <div className="flex items-center gap-2"><Switch checked={edit.status === "active"} onCheckedChange={(v) => setEdit({ ...edit, status: v ? "active" : "out_of_stock" })} /><Label>Active</Label></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="save-product">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
